/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { PaypalCheckoutDestination } from './paypal-checkout';

export const PAYPAL_APPROVED_MESSAGE = 'cybersource-paypal-approved';
export const PAYPAL_CANCELLED_MESSAGE = 'cybersource-paypal-cancelled';
export const PAYPAL_FULL_PAGE_SESSION = 'cybersource-paypal-session';
const PAYPAL_READY_BASKET = 'cybersource-paypal-ready-basket';
const PAYPAL_RETURN_DESTINATION = 'cybersource-paypal-return-destination';

export interface PaypalCorrelation {
    sessionsRequestId: string;
    sessionProcessorTid: string;
    checkoutToken: string;
}

function storageAvailable() {
    return typeof sessionStorage !== 'undefined';
}

export function storePaypalCorrelation(correlation: PaypalCorrelation) {
    if (!storageAvailable()) return;
    sessionStorage.setItem(PAYPAL_FULL_PAGE_SESSION, JSON.stringify(correlation));
}

export function readPaypalCorrelation(): PaypalCorrelation | null {
    if (!storageAvailable()) return null;
    try {
        const value = sessionStorage.getItem(PAYPAL_FULL_PAGE_SESSION);
        if (!value) return null;
        const parsed = JSON.parse(value) as Partial<PaypalCorrelation>;
        if (!parsed.sessionsRequestId || !parsed.sessionProcessorTid || !parsed.checkoutToken) return null;
        return {
            sessionsRequestId: parsed.sessionsRequestId,
            sessionProcessorTid: parsed.sessionProcessorTid,
            checkoutToken: parsed.checkoutToken,
        };
    } catch {
        return null;
    }
}

/** Extract the EC token PayPal returns to the approved/cancelled URL. */
export function getPaypalCheckoutToken(merchantUrl: string): string | null {
    try {
        const token = new URL(merchantUrl.replace(/&amp;/g, '&')).searchParams.get('token');
        return token?.trim() || null;
    } catch {
        return null;
    }
}

export function clearPaypalCorrelation() {
    if (!storageAvailable()) return;
    sessionStorage.removeItem(PAYPAL_FULL_PAGE_SESSION);
}

export function storePaypalReadyBasket(basketId: string, destination: PaypalCheckoutDestination) {
    if (!storageAvailable()) return;
    sessionStorage.setItem(PAYPAL_READY_BASKET, basketId);
    sessionStorage.setItem(PAYPAL_RETURN_DESTINATION, JSON.stringify({ basketId, destination }));
}

export function isPaypalReadyForBasket(basketId: string | undefined) {
    return Boolean(basketId && storageAvailable() && sessionStorage.getItem(PAYPAL_READY_BASKET) === basketId);
}

export function clearPaypalReadyBasket() {
    if (!storageAvailable()) return;
    sessionStorage.removeItem(PAYPAL_READY_BASKET);
    sessionStorage.removeItem(PAYPAL_RETURN_DESTINATION);
}

export function consumePaypalReturnDestination(basketId: string | undefined): PaypalCheckoutDestination | null {
    if (!basketId || !storageAvailable()) return null;
    try {
        const raw = sessionStorage.getItem(PAYPAL_RETURN_DESTINATION);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { basketId?: string; destination?: PaypalCheckoutDestination };
        if (parsed.basketId !== basketId || !parsed.destination) return null;
        sessionStorage.removeItem(PAYPAL_RETURN_DESTINATION);
        return parsed.destination;
    } catch {
        sessionStorage.removeItem(PAYPAL_RETURN_DESTINATION);
        return null;
    }
}
